import React, { useCallback, useMemo } from 'react';
import clsx from 'classnames';
import { SurveyTheme } from '@sm/webassets/dist/components/SurveyTheme/types';
import { createSurveyStyles } from '@sm/webassets';
import { SurveyFormatViewProps } from '../types';
import { isChildFunction, scrollIntoView } from '../utils';
import useFormatView from './useFormatView';
import { deferToAfterFocusChanged } from '~app/pages/SurveyTaking/validation/delayedValidation';
import { useAppDispatch } from '~app/hooks';
import { setActive, setNextActive, setFocus } from '~app/components/Survey/SurveyFormat';
import { isClicking } from '../ActiveViewScrollManager';

const useStyles = createSurveyStyles<'shield', { active: boolean; noShield: boolean; autoPadding: boolean }>(
  (theme: SurveyTheme) => ({
    shield: {
      opacity: ({ active, noShield }) => (!!noShield || active ? 1 : 0.5),
      paddingBottom: ({ autoPadding }) => (autoPadding ? '80px' : 0),
      [`@media (max-width: ${theme.breakpoints?.xs.max})`]: {
        paddingBottom: ({ autoPadding }) => (autoPadding ? '270px' : 0),
      },
      // prevent interaction with clickable elements for inactive questions
      '& [id^="question-title-"]': {
        pointerEvents: ({ active }) => (!active ? 'none' : 'auto'),
      },
      // prevent default browser outline for the focused view
      outline: 'none',
    },
  })
);

export function OQAATFormatView({
  name,
  noShield = false,
  autoPadding = true,
  className,
  children,
  ...props
}: SurveyFormatViewProps): React.ReactElement {
  const dispatch = useAppDispatch();

  const { isActive } = useFormatView(name);

  const { shield } = useStyles({ active: isActive, noShield, autoPadding });

  /** On click, if the current view isn't active, focus it to trigger the focus handler below */
  const handleClick = useCallback(() => {
    if (!isActive) {
      setFocus(name);
    }
  }, [isActive, name]);

  /** Scroll to the focus target, and activate the appropriate view. */
  const handleFocus = useCallback(
    (e: React.FocusEvent) => {
      const target = e.target as HTMLElement;
      const questionTop = document.getElementById(`question-field-${name}`)?.getBoundingClientRect().top;

      // Scroll to the target (either the question or answer) if the question is inactive,
      // OR to any target (incl. links & tooltips) if not a result of clicking,
      // OR to the question itself if the top of the question is above the viewport.
      if (!isActive || !isClicking() || (target.id === `view-${name}` && questionTop && questionTop < 0)) {
        deferToAfterFocusChanged(() => {
          scrollIntoView(target);
        });
      }

      const el = document.getElementById(`view-${name}`);
      if (isActive || !el?.contains(e.target)) {
        return;
      }
      if (noShield) {
        // defer to make done with error-change click work
        deferToAfterFocusChanged(() => {
          dispatch(setActive(name));
        });
        return;
      }
      dispatch(setActive(name));
    },
    [name, isActive, noShield, dispatch]
  );

  // only re-render children if their active state changes
  const content = useMemo(() => {
    if (isChildFunction(children)) {
      return children(isActive, () => dispatch(setNextActive()));
    }
    if (children) {
      return React.cloneElement(children, { active: isActive, onSubmit: () => dispatch(setNextActive()) });
    }
    return null;
  }, [children, dispatch, isActive]);

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events
    <div
      onFocus={handleFocus}
      onClick={noShield ? undefined : handleClick}
      // `offset-target-container` is needed for apps/respweb/views/spa-survey.hbs to calculate initial `marginTop`
      className={clsx('offset-target-container', shield, className)}
      {...props}
      id={`view-${name}`}
      data-testid={`OQAATView__${name}`}
      tabIndex={-1}
    >
      {content}
    </div>
  );
}

export default OQAATFormatView;
